import { motion } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Calendar, MapPin, Users, Star, Image, ChevronRight, Sparkles } from 'lucide-react';
import { DEFAULT_BOOTCAMP_IMAGE } from '../../lib/constants';

const BootcampCard = ({ bootcamp, onViewDetails, onBook }) => {
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const end = new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${start} - ${end}`;
  };

  const spotsLeft = bootcamp.capacity - (bootcamp.booked_count || 0);
  const isAlmostFull = spotsLeft <= 5 && spotsLeft > 0;
  const isFull = spotsLeft <= 0;
  
  const imageUrl = bootcamp.image_url || DEFAULT_BOOTCAMP_IMAGE;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      className="group"
    >
      <Card 
        className="bg-neutral-900 border-neutral-800 hover:border-purple-500/50 transition-all overflow-hidden h-full flex flex-col cursor-pointer"
        onClick={onViewDetails}
        data-testid={`bootcamp-card-${bootcamp.id}`}
      >
        {/* Cover Image */}
        <div className="h-48 overflow-hidden relative">
          <img 
            src={imageUrl} 
            alt={bootcamp.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.target.src = DEFAULT_BOOTCAMP_IMAGE; }}
          />
          {bootcamp.gallery && bootcamp.gallery.length > 0 && (
            <Badge className="absolute bottom-2 right-2 bg-black/70 text-white">
              <Image className="w-3 h-3 mr-1" />
              {bootcamp.gallery.length + 1} photos
            </Badge>
          )}
        </div>
        
        <CardContent className="p-6 flex-1 flex flex-col">
          <h3 className="text-xl font-bold text-white mb-2">{bootcamp.name}</h3>
          
          {bootcamp.description && (
            <p className="text-white/60 text-sm mb-4 line-clamp-2">{bootcamp.description}</p>
          )}
          
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-3 text-white/70 text-sm">
              <Calendar className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span>{formatDateRange(bootcamp.start_date, bootcamp.end_date)}</span>
            </div>
            
            <div className="flex items-center gap-3 text-white/70 text-sm">
              <MapPin className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span>{bootcamp.location}</span>
            </div>
            
            <div className="flex items-center gap-3 text-white/70 text-sm">
              <Users className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span>Ages {bootcamp.age_min}-{bootcamp.age_max}</span>
            </div>
          </div>
          
          {/* Guest Teachers Preview */}
          {bootcamp.guest_teachers && bootcamp.guest_teachers.length > 0 && (
            <div className="mb-4 pb-4 border-b border-neutral-800">
              <p className="text-xs text-purple-400 uppercase tracking-wider mb-2">Featured Teachers</p>
              <div className="flex -space-x-2">
                {bootcamp.guest_teachers.slice(0, 4).map((teacher, idx) => (
                  teacher.image_url ? (
                    <img 
                      key={idx}
                      src={teacher.image_url} 
                      alt={teacher.name}
                      className="w-8 h-8 rounded-full border-2 border-neutral-900 object-cover"
                      title={teacher.name}
                    />
                  ) : (
                    <div 
                      key={idx}
                      className="w-8 h-8 rounded-full border-2 border-neutral-900 bg-purple-500/30 flex items-center justify-center"
                      title={teacher.name}
                    >
                      <Star className="w-4 h-4 text-purple-400" />
                    </div>
                  )
                ))}
                {bootcamp.guest_teachers.length > 4 && (
                  <div className="w-8 h-8 rounded-full border-2 border-neutral-900 bg-neutral-800 flex items-center justify-center text-xs text-white/70">
                    +{bootcamp.guest_teachers.length - 4}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Features Preview */}
          {bootcamp.features && bootcamp.features.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-4">
              {bootcamp.features.slice(0, 3).map((feature, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs bg-purple-500/10 text-purple-300">
                  {feature}
                </Badge>
              ))}
              {bootcamp.features.length > 3 && (
                <Badge variant="secondary" className="text-xs bg-neutral-800 text-white/50">
                  +{bootcamp.features.length - 3} more
                </Badge>
              )}
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />
          
          {/* Price & Availability */}
          <div className="flex items-center justify-between pt-4 border-t border-neutral-800">
            <div>
              <span className="text-2xl font-bold text-white">£{bootcamp.price}</span>
              <span className="text-white/50 text-sm ml-1">/ week</span>
            </div>
            
            {isFull ? (
              <Badge className="bg-red-500/20 text-red-400">Fully Booked</Badge>
            ) : isAlmostFull ? (
              <Badge className="bg-amber-500/20 text-amber-400">Only {spotsLeft} left!</Badge>
            ) : (
              <Badge className="bg-green-500/20 text-green-400">{spotsLeft} spots available</Badge>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="mt-4 flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1 border-neutral-700 text-white hover:bg-neutral-800"
              onClick={(e) => { e.stopPropagation(); onViewDetails(); }}
            >
              View Details
              <ChevronRight className="ml-1 w-4 h-4" />
            </Button>
            <Button 
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              onClick={(e) => { e.stopPropagation(); onBook(); }}
              disabled={isFull}
              data-testid={`book-bootcamp-${bootcamp.id}`}
            >
              <Sparkles className="mr-1 w-4 h-4" />
              Book Now
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default BootcampCard;
